export const COMMISSION_SCHEDULE_YEAR_COUNT = 11;

export const getYearKeys = () =>
  Array.from({ length: COMMISSION_SCHEDULE_YEAR_COUNT }, (_, index) => `yr${index + 1}`);

export const getYearLabel = (yearNumber) => {
  if (yearNumber >= COMMISSION_SCHEDULE_YEAR_COUNT) {
    return `Y${COMMISSION_SCHEDULE_YEAR_COUNT}+`;
  }
  return `Y${yearNumber}`;
};

export const getYearColumnDefs = () =>
  getYearKeys().map((key, index) => ({
    key,
    label: getYearLabel(index + 1),
  }));

export const buildEmptyRateObject = (defaultValue = 0) => {
  const rates = {};
  for (const key of getYearKeys()) {
    rates[key] = defaultValue;
  }
  return rates;
};

// Stored rates are decimals (0.123456 = 12.3456%). Six places keep four percentage digits.
const RATE_DECIMAL_PLACES = 6;

export const roundCommissionRate = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Number(number.toFixed(RATE_DECIMAL_PLACES));
};

export const percentToDecimal = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return roundCommissionRate(number / 100);
};

export const decimalToPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  const percentFactor = 10 ** (RATE_DECIMAL_PLACES - 2);
  return Math.round(number * 10 ** RATE_DECIMAL_PLACES) / percentFactor;
};

export const computeTotalAndGrossPercent = (basicPercent, overridePercent, extraPercent) => {
  const basic = percentToDecimal(basicPercent);
  const override = percentToDecimal(overridePercent);
  const extra = percentToDecimal(extraPercent);
  const total = roundCommissionRate((override + 1) * basic + extra);
  const gross = roundCommissionRate(Math.min(1, total));
  return {
    total: decimalToPercent(total),
    gross: decimalToPercent(gross),
  };
};

// Each year pays at most 100%. The amount over 100% is added to the next year.
// The last year keeps any remainder so it is not discarded.
export const applyGrossRateCap = (totalRates, yearKeys = getYearKeys()) => {
  const gross = {};
  let carry = 0;
  yearKeys.forEach((key, index) => {
    const total = Number(totalRates?.[key]);
    const amount = roundCommissionRate((Number.isFinite(total) ? total : 0) + carry);
    const isLast = index === yearKeys.length - 1;
    if (isLast) {
      gross[key] = amount;
      carry = 0;
      return;
    }
    gross[key] = roundCommissionRate(Math.min(1, amount));
    carry = roundCommissionRate(Math.max(0, amount - gross[key]));
  });
  return gross;
};

export const grossRateFromScheduleRow = (row) => {
  const total = row?.totalRate;
  const hasTotal = getYearKeys().some((key) => Number(total?.[key]) > 0);
  if (!hasTotal) return row?.grossRate || buildEmptyRateObject(0);
  return applyGrossRateCap(total);
};

export const coverRateApplies = (cover, gross) => {
  const coverRate = Number(cover);
  const grossRate = Number(gross);
  return coverRate > 0 && grossRate > coverRate;
};

export const discountRateEnabled = (row) =>
  row?.hasDiscountRate === true ||
  getYearKeys().some((key) => Number(row?.coverRate?.[key]) > 0);

export const rowHasCoverRate = (row) => discountRateEnabled(row);

export const scheduleHasCoverRate = (commissionTable) =>
  (commissionTable || []).some(rowHasCoverRate);

// Consultants see the discount rate for every year when the row has one, including 0.
export const consultantVisibleRate = (row) => {
  const gross = { ...(grossRateFromScheduleRow(row) || buildEmptyRateObject(0)) };
  if (!discountRateEnabled(row)) return gross;
  for (const key of getYearKeys()) {
    const cover = Number(row?.coverRate?.[key]);
    gross[key] = Number.isFinite(cover) ? cover : 0;
  }
  return gross;
};

export const computeScheduleRatePercents = (basicRate, overrideRate, extraRate) => {
  const totalDecimal = {};
  const total = {};
  for (const key of getYearKeys()) {
    const basic = percentToDecimal(basicRate?.[key]);
    const override = percentToDecimal(overrideRate?.[key]);
    const extra = percentToDecimal(extraRate?.[key]);
    const totalValue = roundCommissionRate((override + 1) * basic + extra);
    totalDecimal[key] = totalValue;
    total[key] = decimalToPercent(totalValue);
  }
  const grossDecimal = applyGrossRateCap(totalDecimal);
  const gross = {};
  for (const key of getYearKeys()) {
    gross[key] = decimalToPercent(grossDecimal[key]);
  }
  return { total, gross };
};
